/**
 * Scroll-driven motif effects
 *
 * Scrubs artwork as the page scrolls, rather than playing a timed animation
 * once, so everything tracks the scrollbar in both directions.
 *
 * [data-draw]   0–1 in --draw-progress. CSS wipes the artwork in with a
 *               clip-path; "ltr" (default) left-to-right, "rtl" right-to-left.
 *               Completes over half a viewport once the element is near the
 *               bottom of the screen, so the drawing finishes while it reads.
 *               Once drawn it stays drawn — scrolling back up does not
 *               un-draw it, since a line that redraws itself on every pass
 *               reads as a glitch rather than an effect.
 * [data-rotate] 0–1 in --rotate-progress, spread across the element's whole
 *               journey through the viewport, so the turn is slow and even.
 * [data-parallax] 0–1 in --parallax-progress over the same full-pass range; CSS
 *               translates it so the artwork drifts slower than the page.
 *
 * Reduced motion, or no JS at all, leaves artwork drawn, unrotated and still.
 */
( function () {
	'use strict';

	var items = [];
	var ticking = false;

	function clamp01( value ) {
		return Math.min( 1, Math.max( 0, value ) );
	}

	function progressFor( item ) {
		var rect = item.el.getBoundingClientRect();
		var scroll = window.pageYOffset || document.documentElement.scrollTop;
		var top = rect.top + scroll;
		var vh = window.innerHeight || document.documentElement.clientHeight;

		if ( item.fullPass ) {
			// Spread across the element's full pass through the viewport: 0 as
			// its top reaches the bottom edge, 1 once its bottom clears the top.
			var travel = Math.max( 1, vh + rect.height );

			return clamp01( ( scroll + vh - top ) / travel );
		}

		// Starts as the element nears the bottom of the viewport (or straight
		// away if it is already on screen at the top of the page) and completes
		// half a viewport of scrolling later.
		var start = Math.max( 0, top - vh * 0.9 );
		var distance = Math.max( 1, vh * 0.5 );

		return clamp01( ( scroll - start ) / distance );
	}

	function update() {
		ticking = false;

		for ( var i = 0; i < items.length; i++ ) {
			var item = items[ i ];

			// A completed draw is left alone, so it never plays backwards.
			if ( item.done ) {
				continue;
			}

			var p = progressFor( item );

			if ( p !== item.last ) {
				item.last = p;
				item.el.style.setProperty( item.prop, p );
			}

			if ( item.latch && 1 === p ) {
				item.done = true;
			}
		}
	}

	function onScroll() {
		if ( ! ticking ) {
			ticking = true;
			window.requestAnimationFrame( update );
		}
	}

	function init() {
		var nodes = document.querySelectorAll( '[data-draw], [data-rotate], [data-parallax]' );

		if ( ! nodes.length ) {
			return;
		}

		if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
			return;
		}

		for ( var i = 0; i < nodes.length; i++ ) {
			var rotate = nodes[ i ].hasAttribute( 'data-rotate' );
			var parallax = nodes[ i ].hasAttribute( 'data-parallax' );
			var prop = '--draw-progress';

			if ( rotate ) {
				prop = '--rotate-progress';
			} else if ( parallax ) {
				prop = '--parallax-progress';
			}

			items.push( {
				el: nodes[ i ],
				// Rotation and parallax read better spread over the element's
				// whole trip through the viewport than over a short window.
				fullPass: rotate || parallax,
				// Only the draw wipe sticks. Rotation and parallax are meant to
				// track the scrollbar in both directions.
				latch: ! ( rotate || parallax ),
				prop: prop,
				last: null,
				done: false
			} );
		}

		update();
		window.addEventListener( 'scroll', onScroll, { passive: true } );
		window.addEventListener( 'resize', onScroll );
		window.addEventListener( 'load', onScroll );
	}

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
}() );
